import { useCallback } from 'react'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { LOG_LEVEL } from '../global/chatLog'
import { useChatState } from '../providers/ChatStateProvider'
import { STATUS_CODE_CONFLICT } from '../services/fsChat/fsChannelEnums'
import { IS_PRODUCTION } from '../global/chatConfig'

export default function useHandleChatErrors({ supportInformation, log } = {}) {
  const [t] = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const { user } = useChatState() || {}
  const name = supportInformation?.name
  const cisId = user?.cisId
  const tooManyRequests = useCallback(
    (extraData) => {
      log({ msg: `${cisId} too many requests`, level: LOG_LEVEL.ERROR }, { httpStatus: 429, ...extraData })

      showStatusOverlay({
        message: t(
          'chat.too-many-requests.error',
          `We're Sorry! We were unable to complete your request because you have surpassed the allowed rate limit of what the FamilySearch Chat service provides.`
        ),
        type: 'warning',
        dismissable: true,
        transient: true,
      })
    },
    [log, cisId, t, showStatusOverlay]
  )

  const somethingWentWrong = useCallback(
    (err, extraData) => {
      log(
        { msg: `Something went wrong, please try again later. ${err}`, level: LOG_LEVEL.ERROR },
        { httpStatus: err?.response?.status, ...extraData }
      )

      showStatusOverlay({
        message: t('chat.error.message', 'Something went wrong, please try again later.'),
        type: 'warning',
        dismissable: true,
        transient: true,
      })
    },
    [log, t, showStatusOverlay]
  )

  const handleUserRestrictedError = useCallback(() => {
    log({ msg: 'fs_managed restricted user tried to access a restricted chat', level: LOG_LEVEL.WARN })
    showStatusOverlay({
      message: t('chat.unavailable-chat-access.error', 'This chat is currently unavailable.'),
      type: 'warning',
      actionLabel: t('common-ui:learn-more.action', 'Learn More'),
      actionCallback: () => {
        window.open(
          'https://www.familysearch.org/help/helpcenter/article/familysearch-chat-frequently-asked-questions-faqs',
          '_blank'
        )
      },
      dismissable: true,
    })
  }, [t, log, showStatusOverlay])

  const handleChatCreateGroupError = useCallback(
    (err, extraData, history) => {
      const status = err?.response?.data?.status

      if (status === 429) {
        tooManyRequests()
      } else if (
        err?.response?.status === STATUS_CODE_CONFLICT ||
        ['Channel type', 'with name', 'already exists'].every((text) => err.response?.data?.message?.indexOf(text) > -1)
      ) {
        log(
          {
            msg: `[ChatCreateGroupError] chat:${name} already created`,
            level: LOG_LEVEL.ERROR,
          },
          extraData
        )

        showStatusOverlay({
          message: t(
            'chat.already-exists.error',
            'A public chat by that name already exists. Please choose a different name and try again.'
          ),
          type: 'warning',
          dismissable: true,
        })
      } else if (status === 403) {
        handleUserRestrictedError()
        history?.push('/chats')
      } else {
        somethingWentWrong(err, extraData)
      }
    },
    [log, t, showStatusOverlay, name, somethingWentWrong, tooManyRequests, handleUserRestrictedError]
  )

  const handleBasicError = useCallback(
    (err, extraData) => {
      if (!IS_PRODUCTION) {
        console.error(err)
      }
      const status = err?.response?.status

      if (status === 429) {
        tooManyRequests(extraData)
      } else {
        somethingWentWrong(err, extraData)
      }
    },
    [somethingWentWrong, tooManyRequests]
  )

  const handleChatCreateChatError = (err, extraData) => {
    const status = err?.response?.status

    if (status === 429) {
      tooManyRequests(extraData)
    } else {
      showStatusOverlay({
        message: t('chat.create-button-disabled.label', 'Unable to Message This User'),
        type: 'warning',
        dismissable: true,
        transient: true,
        actionLabel: t('common-ui:learn-more.action', 'Learn More'),
        actionCallback: () => {
          window.open(
            'https://www.familysearch.org/en/help/helpcenter/article/familysearch-chat-frequently-asked-questions-faqs',
            '_blank'
          )
        },
      })
    }
  }

  return {
    handleChatCreateGroupError,
    handleChatCreateChatError,
    handleUserRestrictedError,
    handleBasicError,
  }
}
